import { Input, Label } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

import {
  FIRMWARE_PCBA_FAMILY_CHOICES,
  firmwareFeaturesForFamily,
  firmwarePcbaFamilyLabel,
  type FirmwareFeature,
  type FirmwarePcbaFamily,
} from "./firmwareApi";

export type FirmwareAppliesFormValue = {
  pcbaFamily: FirmwarePcbaFamily | "";
  feature: FirmwareFeature | "";
  revisionEffective: string;
  revisionEnding: string;
};

const selectClassName = cn(
  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
  "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
);

export function appendFirmwareAppliesFormData(form: FormData, value: FirmwareAppliesFormValue): void {
  if (value.pcbaFamily !== "") form.set("pcbaFamily", value.pcbaFamily);
  if (value.feature !== "") form.set("feature", value.feature);
  if (value.revisionEffective.trim() !== "") form.set("revisionEffective", value.revisionEffective.trim());
  if (value.revisionEnding.trim() !== "") form.set("revisionEnding", value.revisionEnding.trim());
}

export function FirmwareAppliesFields({
  value,
  disabled = false,
  onChange,
}: {
  value: FirmwareAppliesFormValue;
  disabled?: boolean;
  onChange: (next: FirmwareAppliesFormValue) => void;
}): JSX.Element {
  const familyValue = value.pcbaFamily === "fi5a" ? "1485" : value.pcbaFamily;
  const features = firmwareFeaturesForFamily(familyValue === "" ? null : familyValue);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <Label htmlFor="firmware-pcba-family">Family</Label>
        <select
          id="firmware-pcba-family"
          className={cn(selectClassName, "mt-1.5")}
          value={familyValue}
          disabled={disabled}
          onChange={(event) => {
            const pcbaFamily = event.target.value as FirmwarePcbaFamily | "";
            const allowed = firmwareFeaturesForFamily(pcbaFamily === "" ? null : pcbaFamily);
            const feature = allowed.includes(value.feature as FirmwareFeature) ? value.feature : "";
            onChange({ ...value, pcbaFamily, feature });
          }}
        >
          <option value="">Select family</option>
          {FIRMWARE_PCBA_FAMILY_CHOICES.map((family) => (
            <option key={family} value={family}>
              {firmwarePcbaFamilyLabel(family)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor="firmware-feature">Feature</Label>
        <select
          id="firmware-feature"
          className={cn(selectClassName, "mt-1.5")}
          value={value.feature}
          disabled={disabled || features.length === 0}
          onChange={(event) => onChange({ ...value, feature: event.target.value as FirmwareFeature | "" })}
        >
          <option value="">{features.length === 0 ? "None" : "Select feature"}</option>
          {features.map((feature) => (
            <option key={feature} value={feature}>
              {feature}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor="firmware-revision-effective">Effective</Label>
        <Input
          id="firmware-revision-effective"
          className="mt-1.5"
          value={value.revisionEffective}
          disabled={disabled}
          placeholder="R12"
          onChange={(event) => onChange({ ...value, revisionEffective: event.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="firmware-revision-ending">Ending</Label>
        <Input
          id="firmware-revision-ending"
          className="mt-1.5"
          value={value.revisionEnding}
          disabled={disabled}
          placeholder="R13"
          onChange={(event) => onChange({ ...value, revisionEnding: event.target.value })}
        />
      </div>
    </div>
  );
}
